import { z } from 'zod';
import {
  BaseResponseSchema,
  dateSchema,
  uuidSchema,
} from './primitives.js';

// ========================================
// Unified Subscription Schemas (New Architecture)
// ========================================

// Billing cycle enum
export const BillingCycleSchema = z.enum(['monthly', 'yearly']);

// Create subscription (assigned or unassigned)
export const CreateSubscriptionRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  tenantId: uuidSchema.optional(), // NULL = unassigned
  maxUsers: z.number().int().min(0).default(999999999),
  maxContainers: z.number().int().min(0).default(999999999),
  maxWafers: z.number().int().min(0).default(999999999),
  maxAxonGateways: z.number().int().min(0).default(999999999),
  maxStorageTb: z.coerce.number().min(0).default(1.0),
  monthlyPrice: z.coerce.number().min(0),
  yearlyPrice: z.coerce.number().min(0).optional(),
  billingCycle: BillingCycleSchema.default('monthly'),
  baseStorageTb: z.coerce.number().min(0).default(0),
  pricePerTb: z.coerce.number().min(0).default(0),
  baseAxonGateways: z.number().int().min(0).default(0),
  pricePerAxonGateway: z.coerce.number().min(0).default(0),
  baseUsers: z.number().int().min(0).default(0),
  pricePerUser: z.coerce.number().min(0).default(0),
  baseWafers: z.number().int().min(0).default(0),
  pricePer1000Wafers: z.coerce.number().min(0).default(0),
  baseContainers: z.number().int().min(0).default(0),
  pricePerContainer: z.coerce.number().min(0).default(0),
  features: z.record(z.unknown()).default({}),
  metadata: z.record(z.unknown()).default({}),
  stripePriceId: z.string().optional(),
  trialStartsAt: dateSchema.optional(), // Trial period start date (defaults to assignment date if not specified)
  trialEndsAt: dateSchema.optional(), // Trial period end date
  requiresPayment: z.boolean().default(true), // If false, allows activation without payment info
  stripeCustomerId: z.string().optional(), // Auto-created when tenant adds payment method
  stripeSubscriptionId: z.string().optional(), // Auto-created when subscription is activated with payment
  providerNotes: z.string().max(5000).optional()
});

// Update subscription
export const UpdateSubscriptionRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  tenantId: uuidSchema.nullable().optional(), // Can assign/unassign
  maxUsers: z.number().int().min(0).optional(),
  maxContainers: z.number().int().min(0).optional(),
  maxWafers: z.number().int().min(0).optional(),
  maxAxonGateways: z.number().int().min(0).optional(),
  maxStorageTb: z.coerce.number().min(0).optional(),
  monthlyPrice: z.coerce.number().min(0).optional(),
  yearlyPrice: z.coerce.number().min(0).optional(),
  billingCycle: BillingCycleSchema.optional(),
  baseStorageTb: z.coerce.number().min(0).optional(),
  pricePerTb: z.coerce.number().min(0).optional(),
  baseAxonGateways: z.number().int().min(0).optional(),
  pricePerAxonGateway: z.coerce.number().min(0).optional(),
  baseUsers: z.number().int().min(0).optional(),
  pricePerUser: z.coerce.number().min(0).optional(),
  baseWafers: z.number().int().min(0).optional(),
  pricePer1000Wafers: z.coerce.number().min(0).optional(),
  baseContainers: z.number().int().min(0).optional(),
  pricePerContainer: z.coerce.number().min(0).optional(),
  features: z.record(z.unknown()).optional(),
  metadata: z.record(z.unknown()).optional(),
  stripePriceId: z.string().optional(),
  trialStartsAt: dateSchema.optional(), // Trial period start date
  trialEndsAt: dateSchema.optional(), // Trial period end date
  requiresPayment: z.boolean().optional(), // If false, allows activation without payment info
  providerNotes: z.string().max(5000).optional()
});

// Duplicate subscription
export const DuplicateSubscriptionRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  tenantId: uuidSchema.optional(), // Optionally assign during duplication
  maxUsers: z.number().int().min(0).optional(),
  maxContainers: z.number().int().min(0).optional(),
  maxWafers: z.number().int().min(0).optional(),
  maxAxonGateways: z.number().int().min(0).optional(),
  maxStorageTb: z.coerce.number().min(0).optional(),
  monthlyPrice: z.coerce.number().min(0).optional(),
  yearlyPrice: z.coerce.number().min(0).optional(),
  billingCycle: BillingCycleSchema.optional(),
  baseStorageTb: z.coerce.number().min(0).optional(),
  pricePerTb: z.coerce.number().min(0).optional(),
  baseAxonGateways: z.number().int().min(0).optional(),
  pricePerAxonGateway: z.coerce.number().min(0).optional(),
  baseUsers: z.number().int().min(0).optional(),
  pricePerUser: z.coerce.number().min(0).optional(),
  baseWafers: z.number().int().min(0).optional(),
  pricePer1000Wafers: z.coerce.number().min(0).optional(),
  baseContainers: z.number().int().min(0).optional(),
  pricePerContainer: z.coerce.number().min(0).optional(),
  features: z.record(z.unknown()).optional(),
  metadata: z.record(z.unknown()).optional(),
  stripePriceId: z.string().optional(),
  trialStartsAt: dateSchema.optional(),
  trialEndsAt: dateSchema.optional(),
  requiresPayment: z.boolean().optional(),
  stripeCustomerId: z.string().optional(),
  stripeSubscriptionId: z.string().optional(),
  providerNotes: z.string().max(5000).optional()
});

// List subscriptions
export const ListSubscriptionsRequestSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  tenantId: uuidSchema.optional(),
  unassigned: z.coerce.boolean().optional(), // Filter for unassigned subscriptions
  status: z.string().optional(),
  isActive: z.coerce.boolean().optional(),
  search: z.string().optional()
});

// Assign to tenant (legacy)
export const AssignSubscriptionRequestSchema = z.object({
  tenantId: uuidSchema
});

// Assign Stripe subscription to tenant (provider route)
export const AssignStripeSubscriptionRequestSchema = z.object({
  tenantId: uuidSchema,
  productId: z.string(),
  priceIds: z.array(z.string()).min(1, 'At least one price ID is required'),
  trialStartDate: dateSchema.optional(),
  trialEndDate: dateSchema.optional(),
  startDate: dateSchema.optional(),
  metadata: z.record(z.string()).optional(),
});

export const AssignStripeSubscriptionResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    stripeSubscriptionId: z.string(),
    stripeCustomerId: z.string(),
    status: z.string(),
    currentPeriodStart: dateSchema,
    currentPeriodEnd: dateSchema,
    trialEnd: dateSchema.nullable(),
    stripeDashboardUrl: z.string(),
  }),
  message: z.string(),
});




// List Stripe Subscriptions schemas
export const ListStripeSubscriptionsRequestSchema = z.object({
  tenantId: uuidSchema.optional(),
  status: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export const StripeSubscriptionItemSchema = z.object({
  subscriptionId: z.string().uuid(),
  billingMode: z.enum(['stripe', 'enterprise_manual']),
  stripeSubscriptionId: z.string(),
  stripeCustomerId: z.string(),
  tenantId: uuidSchema.nullable(),
  tenantName: z.string(),
  productName: z.string(),
  status: z.string(),
  dbStatus: z.string().optional(),
  hasAccess: z.boolean().optional(),
  accessReason: z.string().nullable().optional(),
  currentPeriodStart: z.coerce.date().nullable(),
  currentPeriodEnd: z.coerce.date().nullable(),
  trialEnd: z.coerce.date().nullable(),
  cancelAt: z.coerce.date().nullable(),
  monthlyAmount: z.number(),
  stripeDashboardUrl: z.string(),
  contractPoNumber: z.string().nullable().optional(),
  invoiceDueAt: z.coerce.date().nullable().optional(),
});

export const ListStripeSubscriptionsResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    subscriptions: z.array(StripeSubscriptionItemSchema),
    total: z.number(),
    hasMore: z.boolean(),
    page: z.number(),
    limit: z.number(),
  }),
});





const enterpriseSubscriptionLimitsSchema = z.object({
  maxUsers: z.number().int().min(1),
  maxContainers: z.number().int().min(1),
  maxWafers: z.number().int().min(1),
  maxAxonGateways: z.number().int().min(0),
  maxStorageTb: z.number().min(0),
});

export const ExtendEnterprisePilotRequestSchema = z.object({
  tenantId: uuidSchema,
  additionalDays: z.number().int().min(1).max(90),
  providerNotes: z.string().max(2000).optional(),
});

export const RecordEnterprisePaymentRequestSchema = z.object({
  tenantId: uuidSchema,
  amountUsd: z.number().min(0).optional(),
  paymentReference: z.string().max(256).optional(),
  paymentDate: dateSchema.optional(),
  extendPeriodYears: z.number().int().min(1).max(5).optional(),
});

export const EnterpriseTenantActionRequestSchema = z.object({
  tenantId: uuidSchema,
  reason: z.string().max(500).optional(),
});

export const EnterpriseSubscriptionDataSchema = z.object({
  subscriptionId: uuidSchema,
  tenantId: uuidSchema,
  status: z.string(),
  billingMode: z.enum(['stripe', 'enterprise_manual']),
  trialStartsAt: dateSchema.nullable(),
  trialEndsAt: dateSchema.nullable(),
  currentPeriodStart: dateSchema.nullable(),
  currentPeriodEnd: dateSchema.nullable(),
  invoiceDueAt: dateSchema.nullable(),
  contractPoNumber: z.string().nullable(),
  zohoCustomerId: z.string().nullable(),
  limits: enterpriseSubscriptionLimitsSchema,
  metadata: z.record(z.unknown()),
});

export const EnterpriseSubscriptionResponseSchema = z.object({
  success: z.boolean(),
  data: EnterpriseSubscriptionDataSchema.optional(),
  message: z.string(),
});







export const AssignEnterprisePackageRequestSchema = z.object({
  tenantId: uuidSchema,
  packageId: uuidSchema,
});

const enterprisePackageKindSchema = z.enum(['pilot', 'annual', 'amendment']);

const enterpriseLimitsPatchSchema = enterpriseSubscriptionLimitsSchema.partial();

export const EnterprisePackageDataSchema = z.object({
  packageId: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  kind: enterprisePackageKindSchema,
  tenantId: uuidSchema.nullable(),
  tenantName: z.string().nullable().optional(),
  trialStartsAt: dateSchema.nullable(),
  trialEndsAt: dateSchema.nullable(),
  contractPeriodStart: dateSchema.nullable(),
  contractPeriodEnd: dateSchema.nullable(),
  invoiceDueAt: dateSchema.nullable(),
  invoiceDueDays: z.number().int().nullable(),
  contractPoNumber: z.string().nullable(),
  zohoCustomerId: z.string().nullable(),
  limits: enterpriseSubscriptionLimitsSchema,
  limitsPatch: enterpriseLimitsPatchSchema.nullable().optional(),
  features: z.record(z.unknown()),
  internalNotes: z.string().nullable(),
  active: z.boolean(),
  assignedSubscriptionId: uuidSchema.nullable(),
  assignedAt: dateSchema.nullable(),
  createdAt: dateSchema.optional(),
  updatedAt: dateSchema.optional(),
});

export const ListEnterprisePackagesRequestSchema = z.object({
  kind: enterprisePackageKindSchema.optional(),
  tenantId: uuidSchema.optional(),
  active: z.coerce.boolean().optional(),
  assignableOnly: z.coerce.boolean().optional(),
});

export const ListEnterprisePackagesResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    packages: z.array(EnterprisePackageDataSchema),
  }),
});

export const EnterprisePackageMutationResponseSchema = z.object({
  success: z.boolean(),
  data: EnterprisePackageDataSchema.optional(),
  message: z.string().optional(),
});

export const CreateEnterprisePackageRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  kind: enterprisePackageKindSchema,
  tenantId: uuidSchema.nullable().optional(),
  trialStartsAt: dateSchema.nullable().optional(),
  trialEndsAt: dateSchema.nullable().optional(),
  contractPeriodStart: dateSchema.nullable().optional(),
  contractPeriodEnd: dateSchema.nullable().optional(),
  invoiceDueAt: dateSchema.nullable().optional(),
  invoiceDueDays: z.number().int().min(1).max(120).nullable().optional(),
  contractPoNumber: z.string().max(128).nullable().optional(),
  zohoCustomerId: z.string().max(128).nullable().optional(),
  limits: enterpriseSubscriptionLimitsSchema.optional(),
  limitsPatch: enterpriseLimitsPatchSchema.optional(),
  features: z.record(z.unknown()).optional(),
  internalNotes: z.string().max(4000).nullable().optional(),
});

export const UpdateEnterprisePackageRequestSchema = CreateEnterprisePackageRequestSchema.partial().extend({
  active: z.boolean().optional(),
});









export const GetEnterprisePackageResponseSchema = z.object({
  success: z.literal(true),
  data: EnterprisePackageDataSchema,
});


export const ArchiveEnterprisePackageResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});


// Subscription response schema
// Note: Using z.coerce.number() for decimal fields because TypeORM returns them as strings from PostgreSQL
export const SubscriptionSchema = z.object({
  id: uuidSchema,
  name: z.string().nullable(),
  tenantId: uuidSchema.nullable(),
  maxUsers: z.coerce.number(),
  maxContainers: z.coerce.number(),
  maxWafers: z.coerce.number(),
  maxAxonGateways: z.coerce.number(),
  maxStorageTb: z.coerce.number(),
  monthlyPrice: z.coerce.number(),
  yearlyPrice: z.coerce.number().nullable(),
  billingCycle: z.string(),
  baseStorageTb: z.coerce.number(),
  pricePerTb: z.coerce.number(),
  baseAxonGateways: z.coerce.number(),
  pricePerAxonGateway: z.coerce.number(),
  baseUsers: z.coerce.number(),
  pricePerUser: z.coerce.number(),
  pricePer1000Wafers: z.coerce.number(),
  baseWafers: z.coerce.number().optional(),
  baseContainers: z.coerce.number().optional(),
  pricePerContainer: z.coerce.number().optional(),
  status: z.string(),
  features: z.record(z.unknown()),
  metadata: z.record(z.unknown()),
  currentPeriodStart: dateSchema.nullable(),
  currentPeriodEnd: dateSchema.nullable(),
  trialStartsAt: dateSchema.nullable(),
  trialEndsAt: dateSchema.nullable(),
  requiresPayment: z.boolean(),
  paymentMethodAttached: z.boolean().optional(), // True if tenant has attached a payment method
  paymentValidated: z.boolean().optional(), // True if payment method has been validated
  stripeCustomerId: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  providerNotes: z.string().nullable(),
  stripePriceId: z.string().nullable(),
  currency: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema
});

// Response schemas
export const UnifiedSubscriptionResponseSchema = BaseResponseSchema.extend({
  data: SubscriptionSchema
});

export const UnifiedSubscriptionsListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    subscriptions: z.array(SubscriptionSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number()
  })
});

// Type exports










// ========================================
// Billing & Usage Schemas (Tenant-Facing Billing Page)
// ========================================

// Billing page usage display
export const BillingUsageResponseSchema = z.object({
  users: z.object({
    current: z.number(),
    limit: z.number(),
    percentage: z.number(),
  }),
  wafers: z.object({
    current: z.number(),
    limit: z.number(),
    percentage: z.number(),
  }),
  containers: z.object({
    current: z.number(),
    limit: z.number(),
    percentage: z.number(),
  }),
  axonGateways: z.object({
    current: z.number(),
    limit: z.number(),
    percentage: z.number(),
  }),
  storage: z.object({
    currentTb: z.number(),
    limitTb: z.number(),
    percentage: z.number(),
  }),
});

// Billing page overage preview
export const BillingOveragePreviewResponseSchema = z.object({
  hasOverage: z.boolean(),
  overageDetails: z.object({
    users: z.object({
      overage: z.number(),
      cost: z.number(),
    }).optional(),
    wafers: z.object({
      overage: z.number(),
      cost: z.number(),
    }).optional(),
    containers: z.object({
      overage: z.number(),
      cost: z.number(),
    }).optional(),
    axonGateways: z.object({
      overage: z.number(),
      cost: z.number(),
    }).optional(),
    storage: z.object({
      overageTb: z.number(),
      cost: z.number(),
    }).optional(),
  }),
  totalOverageCost: z.number(),
  currency: z.string(),
});

// Stripe portal session response
export const CreateBillingPortalSessionRequestSchema = z.object({
  returnUrl: z.string().url('Invalid return URL'),
});

export const BillingPortalSessionResponseSchema = z.object({
  url: z.string().url(),
});

// Subscription notification banner
export const TenantBillingSummaryResponseSchema = z.object({
  billingMode: z.enum(['stripe', 'enterprise_manual']),
  status: z.string(),
  showStripePortal: z.boolean(),
  packageName: z.string().nullable(),
  trialEndsAt: dateSchema.nullable(),
  currentPeriodEnd: dateSchema.nullable(),
  invoiceDueAt: dateSchema.nullable(),
  contractPoNumber: z.string().nullable(),
  contactEmail: z.string(),
  invoicePortalUrl: z.string().url().nullable(),
});



export const BillingNotificationResponseSchema = z.object({
  show: z.boolean(),
  severity: z.enum(['warning', 'error', 'info']),
  message: z.string(),
  daysRemaining: z.number().nullable(),
  actionUrl: z.string().optional(),
  actionLabel: z.string().optional(),
});
// Inferred types
export type ArchiveEnterprisePackageResponse = z.infer<typeof ArchiveEnterprisePackageResponseSchema>;
export type AssignEnterprisePackageRequest = z.infer<typeof AssignEnterprisePackageRequestSchema>;
export type AssignStripeSubscriptionRequest = z.infer<typeof AssignStripeSubscriptionRequestSchema>;
export type AssignStripeSubscriptionResponse = z.infer<typeof AssignStripeSubscriptionResponseSchema>;
export type AssignSubscriptionRequest = z.infer<typeof AssignSubscriptionRequestSchema>;
export type BillingCycle = z.infer<typeof BillingCycleSchema>;
export type BillingNotificationResponse = z.infer<typeof BillingNotificationResponseSchema>;
export type BillingOveragePreviewResponse = z.infer<typeof BillingOveragePreviewResponseSchema>;
export type BillingPortalSessionResponse = z.infer<typeof BillingPortalSessionResponseSchema>;
export type BillingUsageResponse = z.infer<typeof BillingUsageResponseSchema>;
export type CreateBillingPortalSessionRequest = z.infer<typeof CreateBillingPortalSessionRequestSchema>;
export type CreateEnterprisePackageRequest = z.infer<typeof CreateEnterprisePackageRequestSchema>;
export type CreateSubscriptionRequest = z.infer<typeof CreateSubscriptionRequestSchema>;
export type DuplicateSubscriptionRequest = z.infer<typeof DuplicateSubscriptionRequestSchema>;
export type EnterprisePackageData = z.infer<typeof EnterprisePackageDataSchema>;
export type EnterprisePackageMutationResponse = z.infer<typeof EnterprisePackageMutationResponseSchema>;
export type EnterpriseSubscriptionData = z.infer<typeof EnterpriseSubscriptionDataSchema>;
export type EnterpriseSubscriptionResponse = z.infer<typeof EnterpriseSubscriptionResponseSchema>;
export type EnterpriseTenantActionRequest = z.infer<typeof EnterpriseTenantActionRequestSchema>;
export type ExtendEnterprisePilotRequest = z.infer<typeof ExtendEnterprisePilotRequestSchema>;
export type GetEnterprisePackageResponse = z.infer<typeof GetEnterprisePackageResponseSchema>;
export type ListEnterprisePackagesRequest = z.infer<typeof ListEnterprisePackagesRequestSchema>;
export type ListEnterprisePackagesResponse = z.infer<typeof ListEnterprisePackagesResponseSchema>;
export type ListStripeSubscriptionsRequest = z.infer<typeof ListStripeSubscriptionsRequestSchema>;
export type ListStripeSubscriptionsResponse = z.infer<typeof ListStripeSubscriptionsResponseSchema>;
export type ListSubscriptionsRequest = z.infer<typeof ListSubscriptionsRequestSchema>;
export type RecordEnterprisePaymentRequest = z.infer<typeof RecordEnterprisePaymentRequestSchema>;
export type StripeSubscriptionItem = z.infer<typeof StripeSubscriptionItemSchema>;
export type Subscription = z.infer<typeof SubscriptionSchema>;
export type TenantBillingSummaryResponse = z.infer<typeof TenantBillingSummaryResponseSchema>;
export type UnifiedSubscriptionResponse = z.infer<typeof UnifiedSubscriptionResponseSchema>;
export type UnifiedSubscriptionsListResponse = z.infer<typeof UnifiedSubscriptionsListResponseSchema>;
export type UpdateEnterprisePackageRequest = z.infer<typeof UpdateEnterprisePackageRequestSchema>;
export type UpdateSubscriptionRequest = z.infer<typeof UpdateSubscriptionRequestSchema>;
